// MdpOnboarding / IntercashOnboarding / GscOnboarding.cshtml: the same KYC form, submitted to that processor.
// Added: CentralPaymentsOnboarding — the SSN instead of an ID document, then the processor's own identity check
// (multiple-choice questions, or a review) before the card is issued. The questions are never kept by QOR: they are
// asked of the processor whenever the page needs them.
import { refreshSession, useSetSession } from '@qor/web-shared';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  answerKyc,
  cardApi,
  kycState,
  onboard,
  type KycState,
  type OnboardingForm,
  type OnboardingProvider,
} from '../api';
import { Alert, NO_OUTCOME, type Outcome } from '../components/Alert';
import { usePageTitle } from '../components/CustomerLayout';
import { useUser } from '../components/RequireAuth';
import { COUNTRIES } from '../countries';
import { checkOnboarding, initialForm, MDP_POLL, onboardingRequest } from '../onboarding';
import { landingPath } from '../routing';

const PERSON = 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm0 2c-3.6 0-7 1.8-7 4.5V20h14v-1.5c0-2.7-3.4-4.5-7-4.5Z';
const ID_CARD =
  'M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Zm2 4v2h5V9H6Zm0 4v2h8v-2H6Zm10-4a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Z';
const SHIELD = 'M12 2 4 6v6c0 5 3.4 8.7 8 10 4.6-1.3 8-5 8-10V6l-8-4Z';

const Icon = ({ d }: { d: string }) => (
  <svg viewBox="0 0 24 24" fill="none">
    <path d={d} fill="currentColor" />
  </svg>
);

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export function OnboardingPage({ provider, pollDelayMs }: { provider: OnboardingProvider; pollDelayMs?: number }) {
  usePageTitle('Set Up Your Card');
  const user = useUser();
  const setSession = useSetSession();
  const navigate = useNavigate();
  const [form, setForm] = useState<OnboardingForm>(() => initialForm(user.displayName));
  const [outcome, setOutcome] = useState<Outcome>(NO_OUTCOME);
  const [busy, setBusy] = useState(false);
  const [verification, setVerification] = useState<KycState | null>(null);
  const gsc = provider === 'gsc';
  const cp = provider === 'opencp';

  // Back on the page while the processor is still checking: show that, not the form again.
  useEffect(() => {
    if (!cp) return;
    let live = true;
    void kycState()
      .then((state) => {
        if (live && state.application?.status === 'under_review') setVerification(state);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [cp]);

  async function issued() {
    // the session was made before this cardholder had a card: read it again so the account page does not send
    // them back here
    setSession(await refreshSession());
    navigate('/card');
  }

  const bind = (name: keyof OnboardingForm) => ({
    name,
    value: form[name] ?? '',
    onChange: (e: { target: { value: string } }) => setForm((f) => ({ ...f, [name]: e.target.value })),
  });

  async function submit(e: FormEvent) {
    e.preventDefault();
    const problem = checkOnboarding(user.cardholderId, form, provider);
    if (problem) return setOutcome({ ok: false, message: problem });
    setBusy(true);
    try {
      const result = await onboard(provider, user.cardholderId as string, onboardingRequest(provider, form));
      if (result.verification) {
        setOutcome(NO_OUTCOME);
        setVerification(result.verification);
        return;
      }
      setOutcome({ ok: result.success, message: result.message });
      if (!result.success) return;
      // the session was made before this cardholder had a card: read it again so the account page does not send
      // them back here
      setSession(await refreshSession());
      const reference = result.providerUserReference;
      if (provider === 'mdp' && reference)
        for (let attempt = 0; attempt < MDP_POLL.attempts; attempt++) {
          if ((await cardApi.account(reference))?.isConnected) break;
          await sleep(pollDelayMs ?? MDP_POLL.delayMs);
        }
      navigate('/card');
    } finally {
      setBusy(false);
    }
  }

  // W15: an InterCash cardholder who has a card has nothing to apply for (the API refuses a second card, D32)
  if ((provider === 'intercash' || cp) && !busy && outcome.message === null && landingPath(user) === '/card')
    return <Navigate to="/card" replace />;

  if (verification) return <IdentityCheck state={verification} onChange={setVerification} onIssued={issued} />;

  return (
    <>
      <h1 className="qcw-greeting">Let&apos;s get your card set up</h1>
      <p className="qcw-subgreeting">A few details are all it takes -- most people finish in under five minutes.</p>

      {gsc && (
        <div className="qcw-alert qcw-alert-warn" role="note">
          Proof of concept -- this submits to a demo card system, not a real card processor. No real card is issued.
        </div>
      )}

      <Alert {...outcome} />

      <div className="qcw-onboard-intro">
        <p className="qcw-section-hint" style={{ margin: 0 }}>
          Here&apos;s what we&apos;ll ask for and why -- standard information any card issuer needs to verify your
          identity.
        </p>
        <div className="qcw-onboard-overview">
          <OverviewItem d={PERSON} label="Personal information" hint="Who you are and how to reach you." />
          <OverviewItem d={ID_CARD} label="Identification" hint="A government ID, required by financial regulation." />
          <OverviewItem
            d={SHIELD}
            label="Address"
            hint={
              provider === 'intercash'
                ? 'Where your card will be associated and delivered.'
                : "Where we'll associate your account."
            }
          />
        </div>
      </div>

      <form onSubmit={(e) => void submit(e)}>
        <Section label="Personal information" d={PERSON}>
          <p className="qcw-section-hint">This is how your card and statements will be addressed.</p>
          <div className="qcw-onboard-row">
            <Field label="First name">
              <input {...bind('firstName')} required />
            </Field>
            <Field label="Last name">
              <input {...bind('lastName')} required />
            </Field>
            <Field label="Phone number">
              <input {...bind('phoneNumber')} type="tel" required />
            </Field>
          </div>
          <div className="qcw-onboard-row">
            <Field label="Preferred language">
              <select {...bind('preferredLanguage')}>
                <option value="en">English</option>
                <option value="fr">French</option>
                <option value="es">Spanish</option>
              </select>
            </Field>
            <Field label="Date of birth">
              <input {...bind('dateOfBirth')} type="date" required />
            </Field>
            {!cp && (
              <Field label="Place of birth">
                <input {...bind('placeOfBirth')} placeholder="City, country" required />
              </Field>
            )}
          </div>
          <div className="qcw-onboard-row">
            <Field label="Gender">
              <select {...bind('gender')}>
                <option value="Female">Female</option>
                <option value="Male">Male</option>
                <option value="Other">Other</option>
              </select>
            </Field>
            <Field label="Nationality">
              <CountrySelect {...bind('nationality')} placeholder="Select nationality" />
            </Field>
          </div>
        </Section>

        <Section label="Identification" d={ID_CARD}>
          <p className="qcw-section-hint">
            Required by financial regulation to verify who you are -- kept secure and never shared beyond issuing your
            card.
          </p>
          {cp ? (
            <div className="qcw-onboard-row">
              <Field label="Social Security number">
                <input
                  {...bind('ssn')}
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="123-45-6789"
                  maxLength={11}
                  required
                />
              </Field>
            </div>
          ) : (
            <div className="qcw-onboard-row">
              <Field label="ID type">
                <select {...bind('idType')}>
                  <option value="Passport">Passport</option>
                  <option value="NationalId">National ID</option>
                  <option value="DriversLicense">Driver&apos;s license</option>
                </select>
              </Field>
              <Field label="ID number">
                <input {...bind('idNumber')} required />
              </Field>
              <Field label="ID expiry date">
                <input {...bind('idExpiryDate')} type="date" required />
              </Field>
            </div>
          )}
        </Section>

        <Section
          label="Address"
          d={SHIELD}
          last
          footer={
            <div className="qcw-onboard-submit">
              <span className="qcw-onboard-trust">
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M12 2 4 5v6c0 5.2 3.4 9 8 11 4.6-2 8-5.8 8-11V5l-8-3Zm-1.2 13.4-3.2-3.2 1.4-1.4 1.8 1.8 4.8-4.8 1.4 1.4-6.2 6.2Z"
                    fill="currentColor"
                  />
                </svg>
                {gsc ? 'Demo data only -- proof of concept.' : 'Encrypted and used only to verify your identity.'}
              </span>
              <button type="submit" className="qcw-btn-primary" disabled={busy}>
                Submit application
              </button>
            </div>
          }
        >
          <p className="qcw-section-hint">
            Where we&apos;ll associate your account and, if applicable, ship a physical card.
          </p>
          <div className="qcw-onboard-row">
            <Field label="Country">
              <CountrySelect {...bind('addressCountry')} placeholder="Select country" />
            </Field>
            <Field label="Region / state">
              <input {...bind('addressRegion')} required />
            </Field>
            <Field label="City">
              <input {...bind('addressCity')} required />
            </Field>
          </div>
          <div className="qcw-onboard-row">
            <Field label="Postal code">
              <input {...bind('addressPostalCode')} />
            </Field>
            <Field label="Street">
              <input {...bind('addressStreet')} required />
            </Field>
            <Field label="House / unit number">
              <input {...bind('addressHouseNumber')} required />
            </Field>
          </div>
        </Section>
      </form>
    </>
  );
}

/**
 * The processor's identity check after the application: its multiple-choice questions, a review in progress, or a
 * refusal. Passed: the card is issued and the cardholder goes to their account.
 */
function IdentityCheck({
  state,
  onChange,
  onIssued,
}: {
  state: KycState;
  onChange: (state: KycState) => void;
  onIssued: () => Promise<void>;
}) {
  const [picked, setPicked] = useState<Record<number, number>>({});
  const [outcome, setOutcome] = useState<Outcome>(NO_OUTCOME);
  const [busy, setBusy] = useState(false);
  const approved = state.status === 'approved' && state.application?.status === 'approved';
  const leaving = useRef(false);

  useEffect(() => {
    // once: the session is read again and the cardholder taken to their account
    if (!approved || leaving.current) return;
    leaving.current = true;
    void onIssued();
  }, [approved, onIssued]);

  async function work(call: () => Promise<KycState>) {
    setBusy(true);
    try {
      const next = await call();
      setPicked({});
      setOutcome(NO_OUTCOME);
      onChange(next);
    } catch (err) {
      setOutcome({ ok: false, message: err instanceof Error ? err.message : 'Something went wrong. Try again.' });
    } finally {
      setBusy(false);
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (state.questions.some((q) => picked[q.id] === undefined))
      return setOutcome({ ok: false, message: 'Answer every question.' });
    void work(() => answerKyc(state.questions.map((q) => ({ questionId: q.id, answerId: picked[q.id]! }))));
  }

  return (
    <>
      <h1 className="qcw-greeting">Verify your identity</h1>
      <Alert {...outcome} />
      {state.status === 'questions_required' && state.questions.length > 0 ? (
        <>
          <p className="qcw-subgreeting">
            To finish issuing your card, answer these questions. They come from public records about you.
          </p>
          <form onSubmit={submit}>
            <Section label="Your answers" d={SHIELD} last>
              {state.questions.map((q) => (
                <fieldset key={q.id} className="qcw-kyc-question" style={{ border: 0, padding: 0, margin: '0 0 16px' }}>
                  <legend className="qcw-control-label">{q.question}</legend>
                  {q.answers.map((a) => (
                    <label key={a.id} className="qcw-kyc-answer" style={{ display: 'block', margin: '6px 0' }}>
                      <input
                        type="radio"
                        name={`q${q.id}`}
                        checked={picked[q.id] === a.id}
                        onChange={() => setPicked((p) => ({ ...p, [q.id]: a.id }))}
                      />{' '}
                      {a.answer}
                    </label>
                  ))}
                </fieldset>
              ))}
              <button type="submit" className="qcw-btn-primary" disabled={busy}>
                Submit answers
              </button>
            </Section>
          </form>
        </>
      ) : state.status === 'denied' ? (
        <div className="qcw-alert qcw-alert-warn" role="alert">
          We couldn&apos;t verify your identity, so a card can&apos;t be issued. Please contact support.
        </div>
      ) : approved ? (
        <p className="qcw-subgreeting">Your identity is verified. Opening your account…</p>
      ) : (
        <>
          <p className="qcw-subgreeting">
            We&apos;re checking your details with the card issuer. We&apos;ll e-mail you as soon as your card is ready.
          </p>
          <button type="button" className="qcw-btn-ghost" disabled={busy} onClick={() => void work(kycState)}>
            Check again
          </button>
        </>
      )}
    </>
  );
}

function OverviewItem({ d, label, hint }: { d: string; label: string; hint: string }) {
  return (
    <div className="qcw-onboard-overview-item">
      <span className="qcw-onboard-overview-icon" aria-hidden="true">
        <Icon d={d} />
      </span>
      <div>
        <p className="qcw-onboard-overview-label">{label}</p>
        <p className="qcw-onboard-overview-hint">{hint}</p>
      </div>
    </div>
  );
}

function Section(props: { label: string; d: string; last?: boolean; footer?: ReactNode; children: ReactNode }) {
  return (
    <section className="qcw-section" aria-label={props.label} style={props.last ? { marginBottom: 20 } : undefined}>
      <div className="qcw-section-heading">
        <span className="qcw-section-icon" aria-hidden="true">
          <Icon d={props.d} />
        </span>
        <h2 className="qcw-section-title">{props.label}</h2>
      </div>
      <div className="qcw-section-body">{props.children}</div>
      {props.footer}
    </section>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="qcw-profile-field">
      <span>{label}</span>
      {children}
    </label>
  );
}

function CountrySelect(props: {
  name: string;
  value: string;
  onChange: (e: { target: { value: string } }) => void;
  placeholder: string;
}) {
  return (
    <select name={props.name} value={props.value} onChange={props.onChange}>
      <option value="">{props.placeholder}</option>
      {COUNTRIES.map(([code, name]) => (
        <option key={code} value={code}>
          {name}
        </option>
      ))}
    </select>
  );
}
